import React from 'react';
import { AdminInfluencer, CachedAsset, Currency, InfluencerCost } from '@round/api';
import { asMoney, buildInstagramUserUrl, buildTiktokUserUrl, buildYoutubeUserUrl } from '../../../../utils/utits';
import styles from './AdminCreatorBar.module.css';
import { Button, Image } from '@round/ui-kit';
import cn from 'classnames';

import { ReactComponent as TiktokIcon } from '../../../../assets/TiktokIcon.svg';
import { ReactComponent as InstagramIcon } from '../../../../assets/InstagramIcon.svg';
import { ReactComponent as YoutubeIcon } from '../../../../assets/YoutubeIcon.svg';

type AdminCreatorBarProps = {
    influencer: AdminInfluencer;
    influencerCostData: InfluencerCost[];
    image?: CachedAsset | undefined;
    currency: Currency | undefined;
    onClickEdit: (id: number) => void;
};

const AdminCreatorBar = ({ currency, influencerCostData, influencer, image, onClickEdit }: AdminCreatorBarProps) => {
    const displayName = influencer.name || influencer.tiktok_display_name || influencer.instagram_display_name;
    const userName = influencer.tiktok_username ?? influencer.instagram_username;
    const userNameDisplayValue = userName ? `@${userName}` : '-';

    const tiktokUserLink = influencer.tiktok_username ? buildTiktokUserUrl(influencer.tiktok_username) : null;
    const instagramUserLink = influencer.instagram_username
        ? buildInstagramUserUrl(influencer.instagram_username)
        : null;
    const youtubeUserLink = influencer.youtube_username ? buildYoutubeUserUrl(influencer.youtube_username) : null;

    const tiktokAudioCost = influencerCostData.find(
        (cost) => cost.platform === 'TIKTOK' && cost.campaign_type === 'AUDIO'
    )?.cost;
    const tiktokAudioCostDisplayValue = tiktokAudioCost ? asMoney(tiktokAudioCost, currency, 0) : '-';

    const tiktokBrandCost = influencerCostData.find(
        (cost) => cost.platform === 'TIKTOK' && cost.campaign_type === 'BRAND'
    )?.cost;
    const tiktokBrandCostDisplayValue = tiktokBrandCost ? asMoney(tiktokBrandCost, currency, 0) : '-';

    const instagramAudioCost = influencerCostData.find(
        (cost) => cost.platform === 'INSTAGRAM' && cost.campaign_type === 'AUDIO'
    )?.cost;
    const instagramAudioCostDisplayValue = instagramAudioCost ? asMoney(instagramAudioCost, currency, 0) : '-';

    const instagramBrandCost = influencerCostData.find(
        (cost) => cost.platform === 'INSTAGRAM' && cost.campaign_type === 'BRAND'
    )?.cost;
    const instagramBrandCostDisplayValue = instagramBrandCost ? asMoney(instagramBrandCost, currency, 0) : '-';

    const youtubeAudioCost = influencerCostData.find(
        (cost) => cost.platform === 'YOUTUBE' && cost.campaign_type === 'AUDIO'
    )?.cost;
    const youtubeAudioCostDisplayValue = youtubeAudioCost ? asMoney(youtubeAudioCost, currency, 0) : '-';

    const youtubeBrandCost = influencerCostData.find(
        (cost) => cost.platform === 'YOUTUBE' && cost.campaign_type === 'BRAND'
    )?.cost;
    const youtubeBrandCostDisplayValue = youtubeBrandCost ? asMoney(youtubeBrandCost, currency, 0) : '-';

    return (
        <div className={cn(styles.barWrapper, { [styles.unavailable]: !influencer.is_available_for_booking })}>
            <div className={styles.influencerDetails}>
                <div className={styles.influencerProfile}>
                    <Image
                        className={styles.image}
                        src={image?.cached_url || image?.original_url || ''}
                        alt={displayName || ''}
                    />
                    <div className={styles.influencerInfo}>
                        <p className={styles.displayName}>{displayName}</p>
                        <p className={styles.userName}>{userNameDisplayValue}</p>
                    </div>
                </div>
                <div className={styles.costStatsSection}>
                    <div className={styles.costStat}>
                        <div
                            className={cn(styles.platformIconContainer, { [styles.linkLive]: tiktokUserLink })}
                            onClick={tiktokUserLink ? () => window.open(tiktokUserLink, '_blank') : undefined}
                        >
                            <TiktokIcon className={styles.platformIcon} />
                        </div>

                        <div className={styles.platformCosts}>
                            <div className={styles.platformCostRow}>
                                <p className={styles.platformHeading}>Audio</p>
                                <p className={styles.platformCostValue}>{tiktokAudioCostDisplayValue}</p>
                            </div>
                            <div className={styles.platformCostRow}>
                                <p className={styles.platformHeading}>Brand</p>
                                <p className={styles.platformCostValue}>{tiktokBrandCostDisplayValue}</p>
                            </div>
                        </div>
                    </div>
                    <div className={styles.costStat}>
                        <div
                            className={cn(styles.platformIconContainer, { [styles.linkLive]: instagramUserLink })}
                            onClick={instagramUserLink ? () => window.open(instagramUserLink, '_blank') : undefined}
                        >
                            <InstagramIcon className={styles.platformIcon} />
                        </div>

                        <div className={styles.platformCosts}>
                            <div className={styles.platformCostRow}>
                                <p className={styles.platformHeading}>Audio</p>
                                <p className={styles.platformCostValue}>{instagramAudioCostDisplayValue}</p>
                            </div>
                            <div className={styles.platformCostRow}>
                                <p className={styles.platformHeading}>Brand</p>
                                <p className={styles.platformCostValue}>{instagramBrandCostDisplayValue}</p>
                            </div>
                        </div>
                    </div>
                    <div className={styles.costStat}>
                        <div
                            className={cn(styles.platformIconContainer, { [styles.linkLive]: youtubeUserLink })}
                            onClick={youtubeUserLink ? () => window.open(youtubeUserLink, '_blank') : undefined}
                        >
                            <YoutubeIcon className={styles.platformIcon} />
                        </div>
                        <div className={styles.platformCosts}>
                            <div className={styles.platformCostRow}>
                                <p className={styles.platformHeading}>Audio</p>
                                <p className={styles.platformCostValue}>{youtubeAudioCostDisplayValue}</p>
                            </div>
                            <div className={styles.platformCostRow}>
                                <p className={styles.platformHeading}>Brand</p>
                                <p className={styles.platformCostValue}>{youtubeBrandCostDisplayValue}</p>
                            </div>
                        </div>
                    </div>

                    <div className={styles.bookingsCountColumn}>
                        <span className={styles.bookingsCountLabel}>Bookings</span>
                        <span className={styles.bookingsCount}>{influencer.booking_count}</span>
                    </div>
                </div>
                <Button onClick={() => onClickEdit(influencer.id)} className={styles.editButton}>
                    Edit creator details
                </Button>
            </div>
        </div>
    );
};

export default AdminCreatorBar;
